import { useState, useEffect, useRef } from 'react'
import Quote from '@/components/ui/quote'

import ArrowCircleRight from '@/icons/bwi/arrow-circle-right.svg'
import ArrowCircleLeft from '@/icons/bwi/arrow-circle-left.svg'

import type { EntryFieldTypes } from 'contentful'
import { slugify, cx } from '@/utils/strings'
import RichText from '../ui/rich-text'
import SliderPresentation from '@/components/ui/slider/slider-presentation'

interface SliderProps {
  internalName: string
  headline?: string
  eyebrow?: string
  body?: EntryFieldTypes.RichText
  slides: []
  sliderStyle?: 'Presentation' | 'Default'
  pdfPath?: string
}

export default function Slider({ internalName, body, slides, headline, eyebrow, sliderStyle, pdfPath }: SliderProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  const [slideWidth, setSlideWidth] = useState(0)
  const [trackWidth, setTrackWidth] = useState(0)
  const [sliderWidth, setSliderWidth] = useState(0)
  const [isFocused, setIsFocused] = useState(false)
  const [isLastSlideVisible, setIsLastSlideVisible] = useState(false)
  const sliderRef = useRef<HTMLDivElement>(null)
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])

  // Calculate the total number of slides that can be navigated to
  const totalSlides = slides.length - 1

  // Variables to track touch positions
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)

  // Calculate actual slide widths for precise movement
  useEffect(() => {
    // Initialize refs array
    slideRefs.current = slideRefs.current.slice(0, slides.length)

    const calculateSlideWidths = () => {
      if (slideRefs.current[0]?.offsetWidth) {
        // Get the width of a slide including its padding
        const slideElement = slideRefs.current[0]
        const slideElementWidth = slideElement.offsetWidth
        const sliderWidth = sliderRef.current ? sliderRef.current.offsetWidth : 0
        setSliderWidth(sliderWidth)
        // Calculate the exact percentage this slide takes in the slider
        const exactSlideWidthPercentage = (slideElementWidth / sliderWidth) * 100
        setSlideWidth(exactSlideWidthPercentage)
        const trackWidth = slideElementWidth * slides.length
        setTrackWidth(trackWidth)

        checkLastSlideVisibility(currentIndex, sliderWidth, slideElementWidth, slides.length)

        if (trackWidth - 40 <= sliderWidth && currentIndex > 0) {
          // if the whole track fits on the screen, reset navigation
          setCurrentIndex(0)
        }
      }
    }

    calculateSlideWidths()

    // Recalculate on window resize
    window.addEventListener('resize', calculateSlideWidths)
    return () => window.removeEventListener('resize', calculateSlideWidths)
  }, [slides.length, currentIndex])

  const checkLastSlideVisibility = (index: number, sliderWidth: number, slideWidth: number, totalItems: number) => {
    const test = slideWidth - 4
    const visibleSlidesCount = sliderWidth / test

    const isLastVisible = index + visibleSlidesCount >= totalItems
    setIsLastSlideVisible(isLastVisible)
  }

  useEffect(() => {
    if (slideRefs.current[0]?.offsetWidth) {
      const slideWidth = slideRefs.current[0].offsetWidth
      const sliderWidth = sliderRef.current ? sliderRef.current.offsetWidth : 0
      checkLastSlideVisibility(currentIndex, sliderWidth, slideWidth, slides.length)
    }
  }, [currentIndex, slides.length])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle keyboard events when slider is focused
      if (!isFocused) return

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        prevSlide()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        nextSlide()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [currentIndex, isFocused])

  // Swipe controls
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return

    const deltaX = touchStartX.current - touchEndX.current

    if (deltaX > 50) {
      // Swipe left
      nextSlide()
    } else if (deltaX < -50) {
      // Swipe right
      prevSlide()
    }

    // Reset touch positions
    touchStartX.current = null
    touchEndX.current = null
  }

  const nextSlide = () => {
    if (!isLastSlideVisible) {
      setCurrentIndex((prev) => prev + 1)
    }
  }

  const prevSlide = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1)
    }
  }

  const controlButtonStyles = 'h-[34px] w-[34px]'
  const disabledButtonStyles = 'text-muted cursor-not-allowed'

  return sliderStyle === 'Presentation' ? (
    <section id={slugify(internalName)}>
      {headline && <h3 className="text-center font-normal">{headline}</h3>}
      <SliderPresentation customCss="md:[--bw-slider-nav-button-distance:2.44rem]" pdfPath={pdfPath} slides={slides} />
    </section>
  ) : (
    <section
      className="w-full max-w-7xl mx-auto overflow-hidden focus:outline-none"
      tabIndex={0}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      aria-label="slider"
    >
      <div className={cx('relative', !headline && !eyebrow && !body && 'pb-12')}>
        {(headline || eyebrow || body) && (
          <div className="px-2 mb-8 sm:w-10/12 text-center sm:text-left">
            {eyebrow && <p className="text-eyebrow uppercase">{eyebrow}</p>}
            {headline && <h2>{headline}</h2>}
            {body && <RichText body={body} />}
          </div>
        )}
        <div
          className={cx(
            'right-5 lg:right-9',
            !headline && !eyebrow && !body
              ? 'absolute top-0'
              : 'flex justify-center mb-10 sm:mb-0 sm:absolute bottom-0',
            trackWidth - 40 <= sliderWidth && 'hidden'
          )}
        >
          <div className="flex gap-2">
            <button
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className={cx(controlButtonStyles, currentIndex === 0 ? disabledButtonStyles : 'text-primaryBlue')}
              aria-label="Previous slide"
            >
              <ArrowCircleLeft />
            </button>
            <button
              onClick={nextSlide}
              disabled={isLastSlideVisible}
              className={cx(
                controlButtonStyles,
                currentIndex >= totalSlides || isLastSlideVisible ? disabledButtonStyles : 'text-primaryBlue'
              )}
              aria-label="Next slide"
            >
              <ArrowCircleRight />
            </button>
          </div>
        </div>
      </div>

      <div
        className="relative"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="grid grid-cols-1">
          <div
            ref={sliderRef}
            className="flex flex-row transition-transform duration-500 ease-in-out "
            style={{ transform: `translateX(-${currentIndex * slideWidth}%)` }}
          >
            {slides.map((slide, index) => (
              <div
                key={slide.sys.id}
                ref={(el) => (slideRefs.current[index] = el)}
                className={cx(
                  'h-full flex-shrink-0 px-2 w-[400px] sm:w-[429px]',
                  slides.length === 2 && 'lg:w-[640px]',
                  'first:pl-5 first:lg:pl-9 last:pr-5 last:lg:pr-9',
                )}
              >
                {(() => {
                  switch (slide.sys.contentType.sys.id) {
                    case 'componentQuote':
                      return <Quote className="h-full" {...slide.fields} />
                    default:
                      return <Quote className="h-full" {...slide.fields} />
                  }
                })()}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
